import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";

const API_URL = "/api";

function Dashboard({ onLogout }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/data`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (res.status === 401) {
          if (onLogout) onLogout();
          navigate("/login");
          return;
        }
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        setSessions(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        console.error("Error fetching sessions:", err);
        setError(err.message || "Failed to fetch sessions");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate, onLogout]);

  const normalize = (s) =>
    (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const filteredSessions = useMemo(() => {
    const q = normalize(filter);
    if (!q) return sessions;
    return sessions.filter((session) =>
      [session.track_name, session.driver_name, session.sprocket].some((f) => normalize(f).includes(q))
    );
  }, [filter, sessions]);

  const handleGeneratePDF = async () => {
    try {
      const res = await fetch(
        `${API_URL}/data/pdf?filter=${encodeURIComponent(filter)}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      if (res.status === 401) { if (onLogout) onLogout(); navigate("/login"); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "sessions.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Nie udało się wygenerować PDFa.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Czy na pewno chcesz usunąć tę sesję?")) return;
    try {
      const res = await fetch(`${API_URL}/data/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (res.status === 401) { if (onLogout) onLogout(); navigate("/login"); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Błąd usuwania:", err);
      alert("Nie udało się usunąć sesji.");
    }
  };

  if (loading) return <div className="dashboard-page">Ładowanie danych...</div>;
  if (error) return <div className="dashboard-page">Błąd: {error}</div>;

  return (
    <div className="dashboard-page">
      <div className="dashboard-container" style={{ width: windowWidth > 1024 ? "95%" : "100%" }}>
        <h2>Dashboard</h2>
        <div className="filter-and-buttons">
          <input
            type="text"
            placeholder="Filter by track / driver / sprocket..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-input"
          />
          <button onClick={handleGeneratePDF} className="generate-pdf-button">Generate PDF</button>
          <button onClick={() => navigate("/form")} className="back-button">Back</button>
        </div>

        {filteredSessions.length === 0 ? (
          <div className="empty-text">Brak wyników dla: "{filter}".</div>
        ) : (
          <div className="table-container" style={{ overflowX: windowWidth < 1024 ? "auto" : "hidden" }}>
            <table className="dashboard-table" style={{ minWidth: "1000px" }}>
              <thead>
                <tr>
                  <th>Track Name</th><th>Driver Name</th><th>Sprocket</th><th>Best Lap</th>
                  <th>Cold LF</th><th>Cold RF</th><th>Cold LR</th><th>Cold RR</th>
                  <th>Hot LF</th><th>Hot RF</th><th>Hot LR</th><th>Hot RR</th>
                  <th>Description</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((session, index) => (
                  <tr key={session.id ?? index}>
                    <td>{session.track_name ?? "-"}</td>
                    <td>{session.driver_name ?? "-"}</td>
                    <td>{session.sprocket ?? "-"}</td>
                    <td>{session.best_lap_time ?? "-"}</td>
                    <td>{session.cold_pressure_lf ?? "-"}</td>
                    <td>{session.cold_pressure_rf ?? "-"}</td>
                    <td>{session.cold_pressure_lr ?? "-"}</td>
                    <td>{session.cold_pressure_rr ?? "-"}</td>
                    <td>{session.hot_pressure_lf ?? "-"}</td>
                    <td>{session.hot_pressure_rf ?? "-"}</td>
                    <td>{session.hot_pressure_lr ?? "-"}</td>
                    <td>{session.hot_pressure_rr ?? "-"}</td>
                    <td className="desc-cell">{session.setup_description ?? "-"}</td>
                    <td>
                      <button className="delete-btn" onClick={() => handleDelete(session.id)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
